// =========================================================================
// Vendor Broadcasts service layer.
//
// Backs @pc/modules-vendors's BroadcastComposerPage / BroadcastDetailPage /
// BroadcastsListPage. The actual delivery loop lives in the
// `vendor-broadcast-worker` Edge Function (cron / 1m); this service only
// exposes the operator-facing reads + mutations.
//
// Schema lives at supabase/migrations/20260622020000_vendors_account_executive_and_broadcasts.sql.
// We declare local row interfaces because the generated `Database` type
// regenerates at deploy time and may not yet include the new tables.
// =========================================================================

import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';

// ---- types --------------------------------------------------------------

export type BroadcastStatus =
  | 'draft'
  | 'queued'
  | 'sending'
  | 'completed'
  | 'cancelled'
  | 'failed';

export type BroadcastRecipientStatus =
  | 'pending'
  | 'sent'
  | 'failed'
  | 'skipped';

export type BroadcastChannel = 'sms' | 'email';

export interface VendorBroadcast {
  id: string;
  org_id: string;
  store_id: string;
  initiated_by_user_id: string;
  on_behalf_of_user_id: string;
  send_sms: boolean;
  send_email: boolean;
  sms_body: string | null;
  email_subject: string | null;
  email_html: string | null;
  email_text: string | null;
  status: BroadcastStatus;
  recipients_total: number;
  recipients_sent: number;
  recipients_failed: number;
  recipients_skipped: number;
  pace_per_minute: number;
  scheduled_for: string | null;
  started_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancelled_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface VendorBroadcastRecipient {
  id: string;
  broadcast_id: string;
  org_id: string;
  store_id: string;
  vendor_id: string;
  vendor_contact_id: string | null;
  channel: BroadcastChannel;
  to_phone: string | null;
  to_email: string | null;
  contact_name: string | null;
  status: BroadcastRecipientStatus;
  sms_message_id: string | null;
  email_message_id: string | null;
  skip_reason: string | null;
  error_text: string | null;
  attempts: number;
  next_attempt_at: string;
  created_at: string;
  sent_at: string | null;
  failed_at: string | null;
}

export interface BroadcastPreview {
  vendors_count: number;
  sms_count: number;
  email_count: number;
}

// ---- schema accessor ---------------------------------------------------

function vendorsSchema() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (supabase() as any).schema('vendors');
}

// =========================================================================
// Preview
// =========================================================================

export interface PreviewVendorBroadcastInput {
  onBehalfOfUserId: string;
  sendSms: boolean;
  sendEmail: boolean;
}

export async function previewVendorBroadcast(
  ctx: ActiveContext,
  input: PreviewVendorBroadcastInput,
): Promise<BroadcastPreview> {
  const { orgId, storeId } = requireContext(ctx);
  const { data, error } = await vendorsSchema().rpc('rpc_broadcast_preview', {
    p_org_id: orgId,
    p_store_id: storeId,
    p_on_behalf_of_user_id: input.onBehalfOfUserId,
    p_send_sms: input.sendSms,
    p_send_email: input.sendEmail,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) {
    return { vendors_count: 0, sms_count: 0, email_count: 0 };
  }
  return {
    vendors_count: Number(row.vendors_count ?? 0),
    sms_count: Number(row.sms_count ?? 0),
    email_count: Number(row.email_count ?? 0),
  };
}

// =========================================================================
// Create
// =========================================================================

export interface CreateVendorBroadcastInput {
  onBehalfOfUserId: string;
  sendSms: boolean;
  sendEmail: boolean;
  smsBody?: string | null;
  emailSubject?: string | null;
  emailHtml?: string | null;
  emailText?: string | null;
  pacePerMinute?: number;
  scheduledFor?: string | null;
}

export async function createVendorBroadcast(
  ctx: ActiveContext,
  input: CreateVendorBroadcastInput,
): Promise<{ id: string }> {
  const { orgId, storeId } = requireContext(ctx);
  const { data, error } = await vendorsSchema().rpc('rpc_broadcast_create', {
    p_org_id: orgId,
    p_store_id: storeId,
    p_on_behalf_of_user_id: input.onBehalfOfUserId,
    p_send_sms: input.sendSms,
    p_send_email: input.sendEmail,
    p_sms_body: input.smsBody ?? null,
    p_email_subject: input.emailSubject ?? null,
    p_email_html: input.emailHtml ?? null,
    p_email_text: input.emailText ?? null,
    p_pace_per_minute: input.pacePerMinute ?? 30,
    p_scheduled_for: input.scheduledFor ?? null,
  });
  if (error) throw error;
  if (typeof data !== 'string') {
    throw new Error('rpc_broadcast_create did not return an id.');
  }
  return { id: data };
}

// =========================================================================
// Cancel
// =========================================================================

export async function cancelVendorBroadcast(
  ctx: ActiveContext,
  id: string,
): Promise<void> {
  requireContext(ctx);
  const { error } = await vendorsSchema().rpc('rpc_broadcast_cancel', {
    p_broadcast_id: id,
  });
  if (error) throw error;
}

// =========================================================================
// Get one
// =========================================================================

export async function getVendorBroadcast(
  ctx: ActiveContext,
  id: string,
): Promise<VendorBroadcast | null> {
  requireContext(ctx);
  const { data, error } = await vendorsSchema()
    .from('vendor_broadcasts')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as VendorBroadcast | null;
}

// =========================================================================
// List broadcasts (keyset, store-scoped)
// =========================================================================

export interface ListVendorBroadcastsInput extends KeysetInput {
  status?: BroadcastStatus | BroadcastStatus[];
  initiatedByUserId?: string;
  onBehalfOfUserId?: string;
}

export async function listVendorBroadcasts(
  ctx: ActiveContext,
  input: ListVendorBroadcastsInput = {},
): Promise<ListResult<VendorBroadcast>> {
  const { orgId, storeId } = requireContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = vendorsSchema()
    .from('vendor_broadcasts')
    .select('*', selectCountOption(input.countMode))
    .eq('org_id', orgId)
    .eq('store_id', storeId);

  if (Array.isArray(input.status) && input.status.length > 0) {
    q = q.in('status', input.status);
  } else if (typeof input.status === 'string') {
    q = q.eq('status', input.status);
  }
  if (input.initiatedByUserId) {
    q = q.eq('initiated_by_user_id', input.initiatedByUserId);
  }
  if (input.onBehalfOfUserId) {
    q = q.eq('on_behalf_of_user_id', input.onBehalfOfUserId);
  }

  q = applyKeyset(q, input.cursor, 'created_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as VendorBroadcast[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'created_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

// =========================================================================
// List recipients (keyset, broadcast-scoped)
// =========================================================================

export interface ListVendorBroadcastRecipientsInput extends KeysetInput {
  status?: BroadcastRecipientStatus | BroadcastRecipientStatus[];
  channel?: BroadcastChannel;
}

export async function listVendorBroadcastRecipients(
  ctx: ActiveContext,
  broadcastId: string,
  input: ListVendorBroadcastRecipientsInput = {},
): Promise<ListResult<VendorBroadcastRecipient>> {
  requireContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = vendorsSchema()
    .from('vendor_broadcast_recipients')
    .select('*', selectCountOption(input.countMode))
    .eq('broadcast_id', broadcastId);

  if (Array.isArray(input.status) && input.status.length > 0) {
    q = q.in('status', input.status);
  } else if (typeof input.status === 'string') {
    q = q.eq('status', input.status);
  }
  if (input.channel) q = q.eq('channel', input.channel);

  q = applyKeyset(q, input.cursor, 'created_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as VendorBroadcastRecipient[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'created_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}
